<template>
  <v-app :class="isAppShell ? 'bg-background' : 'bg-surface'">
    <pre class="d-none" data-testid="events">{{ events }}</pre>
    <v-app-bar
      height="80"
      class="px-4 power-up-app-bar"
      flat
      :class="{
        'power-up-app-shell-bar': isAppShell,
        'power-up-app-bar--app': isAppShell,
        'power-up-app-bar--marketing': !isAppShell,
      }"
      :color="isAppShell ? 'secondary' : 'surface'"
      :theme="isAppShell ? 'dark' : 'light'"
    >
      <!--
        Plain flex row: skip v-app-bar-title (it adds title typography / padding that skews vertical alignment).
      -->
      <a
        :href="linkToHrefWithHost(rootLinks.home)"
        class="power-up-logo-link d-flex align-center flex-shrink-0 text-decoration-none mr-3 mr-md-6"
      >
        <img
          id="nav-logo"
          :src="navLogoSrc"
          :alt="t(power_up_layout.logo_alt)"
          class="nav-logo-img"
        />
      </a>

      <v-spacer />

      <template #append>
        <div class="d-flex align-center flex-shrink-0">
          <v-toolbar-items class="d-none d-md-flex align-center">
            <v-btn
              v-for="link in navigationLinks"
              v-if="mounted"
              :key="link.name"
              variant="text"
              :color="isAppShell ? 'white' : 'on-surface'"
              class="text-body-large"
              :class="{ 'tracking-wide': !isAppShell }"
              :href="link.href"
            >
              {{ link.name }}
            </v-btn>
          </v-toolbar-items>

          <!--
            Desktop account hamburger. Sits at the right end of the nav row
            (replacing the old "ACCOUNT" button) and opens a small menu with
            Account / Support / Sign Out. Only rendered when there are user-
            facing account links to surface — `accountMenuLinks` is empty on
            logged-out marketing shells, which keeps the hamburger off there.
          -->
          <v-menu
            v-if="loggedIn && accountMenuLinks.length"
            v-model="accountMenuOpen"
            :close-on-content-click="true"
            location="bottom end"
            offset="8"
            width="220"
          >
            <template #activator="{ props: menuActivatorProps }">
              <v-btn
                v-bind="menuActivatorProps"
                icon
                variant="text"
                :color="isAppShell ? 'white' : 'on-surface'"
                class="d-none d-md-inline-flex"
                :aria-label="t(power_up_layout.account_menu_aria)"
              >
                <v-icon>{{ accountMenuOpen ? "mdi-close" : "mdi-menu" }}</v-icon>
              </v-btn>
            </template>

            <v-card rounded="lg" elevation="8" class="overflow-hidden">
              <v-list bg-color="surface" class="py-0">
                <template v-for="item in accountMenuLinks" :key="item.key">
                  <v-divider v-if="item.divideBefore" />
                  <v-list-item
                    :href="item.href"
                    :rel="item.rel"
                    :class="item.danger ? 'text-error' : ''"
                    @click="accountMenuOpen = false"
                  >
                    <template v-if="item.icon" #prepend>
                      <v-icon
                        :icon="item.icon"
                        :color="item.danger ? 'error' : undefined"
                        size="20"
                        class="mr-1"
                      />
                    </template>
                    <v-list-item-title
                      class="text-body-2"
                      :class="item.danger ? 'text-error' : ''"
                    >
                      {{ item.name }}
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </v-list>
            </v-card>
          </v-menu>

          <v-menu
            v-if="navigationLinks.length"
            v-model="mobileNavOpen"
            :close-on-content-click="true"
            location="bottom end"
            offset="8"
            width="280"
          >
            <template #activator="{ props: menuActivatorProps }">
              <v-app-bar-nav-icon
                v-bind="menuActivatorProps"
                class="d-md-none"
                :color="isAppShell ? 'white' : 'default'"
              >
                <v-icon>{{ mobileNavOpen ? "mdi-close" : "mdi-menu" }}</v-icon>
              </v-app-bar-nav-icon>
            </template>

            <v-card rounded="lg" elevation="8" class="overflow-hidden">
              <nav aria-label="Main">
                <v-list bg-color="surface" class="pt-0">
                  <v-list-item
                    v-for="link in navigationLinks"
                    :key="link.name"
                    :href="link.href"
                    @click="mobileNavOpen = false"
                  >
                    <v-list-item-title class="text-body-2">
                      {{ link.name }}
                    </v-list-item-title>
                  </v-list-item>

                  <!--
                    Account / Support / Sign Out are pulled out of the main
                    nav list on desktop (they live behind the hamburger menu
                    above). Surface them at the bottom of the mobile menu so
                    nothing is unreachable on touch viewports.
                  -->
                  <template v-if="loggedIn && accountMenuLinks.length">
                    <v-divider class="my-1" />
                    <v-list-item
                      v-for="item in accountMenuLinks"
                      :key="`mobile-${item.key}`"
                      :href="item.href"
                      :rel="item.rel"
                      :class="item.danger ? 'text-error' : ''"
                      @click="mobileNavOpen = false"
                    >
                      <template v-if="item.icon" #prepend>
                        <v-icon
                          :icon="item.icon"
                          :color="item.danger ? 'error' : undefined"
                          size="20"
                          class="mr-1"
                        />
                      </template>
                      <v-list-item-title
                        class="text-body-2"
                        :class="item.danger ? 'text-error' : ''"
                      >
                        {{ item.name }}
                      </v-list-item-title>
                    </v-list-item>
                  </template>
                </v-list>
              </nav>

              <v-divider />

              <div class="pa-3">
                <v-btn
                  block
                  color="primary"
                  variant="flat"
                  size="default"
                  class="text-none"
                  rounded="lg"
                  :href="sellPartHref"
                  @click="mobileNavOpen = false"
                >
                  {{ t(power_up_layout.sell_a_part) }}
                </v-btn>
              </div>
            </v-card>
          </v-menu>
        </div>
      </template>
    </v-app-bar>

    <v-main :class="isAppShell ? 'bg-background' : 'bg-surface'">
      <div class="power-up-main-frame">
        <ContentWidth v-if="!disableContainer">
          <slot />
        </ContentWidth>
        <slot v-else />
      </div>
    </v-main>

    <!--
      Footer is a flat flex layout, not a Vuetify v-row / v-col grid: the grid
      version was stacking default-height v-btn elements (~36–44px each) inside
      pa-6 cols and ballooned the footer to several hundred pixels tall on
      desktop. Here the entire footer renders in two short rows: a content row
      (logo + tagline | links | optional sign-up CTA) and a thin copyright
      strip beneath it.
    -->
    <LayoutFooter color="marketingFooter" class="power-up-footer pa-0">
      <v-container class="power-up-footer__inner py-3 py-md-4">
        <div class="power-up-footer__row">
          <a
            :href="linkToHrefWithHost(rootLinks.home)"
            class="power-up-footer__brand d-flex align-center ga-3 text-decoration-none"
          >
            <img
              class="footer-logo-img"
              :src="pax_navbar_logo"
              :alt="t(power_up_layout.logo_alt)"
              width="120"
              height="41"
            />
            <span class="footer-description d-none d-sm-inline">
              {{ t(power_up_layout.footer_description) }}
            </span>
          </a>

          <nav class="power-up-footer__links" aria-label="Footer">
            <a
              class="power-up-footer__link"
              :href="linkToHrefWithHost(rootLinks.ai)"
            >
              Powering AI
            </a>
            <a
              class="power-up-footer__link"
              :href="linkToHrefWithHost(rootLinks.about)"
            >
              About
            </a>
            <a
              class="power-up-footer__link"
              :href="linkToHrefWithHost(rootLinks.contact)"
            >
              Contact Us
            </a>
          </nav>

          <v-btn
            v-if="!loggedIn && mounted"
            color="surface"
            variant="flat"
            size="small"
            rounded="lg"
            class="power-up-footer__cta text-none font-weight-semibold"
            :href="linkToHrefWithHost(authLinks.newRegistration)"
          >
            {{ t(power_up_layout.sign_up) }}
          </v-btn>
        </div>

        <div class="power-up-footer__copyright">
          <i18n-t
            :keypath="lookupTKey(power_up_layout.copyright)"
            scope="global"
            tag="span"
          >
            <template #year>
              {{ new Date().getFullYear() }}
            </template>
          </i18n-t>
        </div>
      </v-container>
    </LayoutFooter>
    <SnackbarQueue />
  </v-app>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { pax_navbar_logo } from "../../assets/index.ts";
import paxNavbarLogoUrl from "../../assets/pax-navbar-logo.svg?url";
/** Navbar mark for the white marketing bar (surface). */
import paxNavbarLogoOnLightUrl from "../../assets/pax-navbar-logo-auth.svg?url";
import { ContentWidth } from "@saflib/vue/components";
import LayoutFooter from "./LayoutFooter.vue";
import { power_up_layout } from "./PowerUpLayout.strings.ts";
import { useReverseT } from "../../i18n.ts";
import { linkToHrefWithHost, navigateToLink } from "@saflib/links";
import { adminLinks, appLinks, rootLinks } from "@vendata/power-up-links";
import { accountLinks } from "@vendata/power-up-links";
import { useKratosSession } from "@saflib/ory-kratos-sdk";
import { authLinks } from "@saflib/ory-kratos-sdk/links";
import { events } from "@saflib/vue";
import { SnackbarQueue } from "@saflib/vue/components";
import { isAdminFromSession } from "../../utils/index.ts";
const props = withDefaults(
  defineProps<{
    loggedIn?: boolean;
    disableContainer?: boolean;
    /**
     * `marketing` — white bar + white main (root site, auth SPA).
     * `app` — navy bar + default grey main (inventory app, account, admin).
     */
    navAppearance?: "marketing" | "app";
    /**
     * When set, replaces the default marketing / logged-in link list (e.g. admin: `[]` for logo-only bar).
     */
    navigationLinksOverride?: { name: string; href: string }[];
  }>(),
  { navAppearance: "marketing" },
);

const isAppShell = computed(() => props.navAppearance === "app");

/** Navy mark on the blue bar; light-background mark on the white bar. */
const navLogoSrc = computed(() =>
  isAppShell.value ? paxNavbarLogoUrl : paxNavbarLogoOnLightUrl,
);

const { t, lookupTKey } = useReverseT();
const mobileNavOpen = ref(false);
const accountMenuOpen = ref(false);

const sellPartHref = linkToHrefWithHost(appLinks.sellPart);

const mounted = ref(false);
onMounted(() => {
  mounted.value = true;
});

const sessionQuery = useKratosSession();

function currentPageReturnTo(): string {
  return typeof window !== "undefined" ? window.location.href : "";
}

/** App shell pages pass `loggedIn`; if the browser has no Kratos session, send them to login. */
watch(
  sessionQuery.isLoading,
  (isLoading) => {
    if (!props.loggedIn) return;
    if (isLoading === false && sessionQuery.data.value?.id === undefined) {
      const returnTo = currentPageReturnTo();
      navigateToLink(authLinks.newLogin, {
        params: returnTo ? { return_to: returnTo } : {},
      });
    }
  },
  { immediate: true },
);

const navigationLinks = computed(() => {
  if (props.navigationLinksOverride !== undefined) {
    return props.navigationLinksOverride;
  }
  return props.loggedIn
    ? [
        {
          name: t(power_up_layout.parts_inventory_home),
          href: linkToHrefWithHost(appLinks.home),
        },
        {
          name: t(power_up_layout.my_portal),
          href: linkToHrefWithHost(appLinks.myPortal),
        },
        {
          name: t(power_up_layout.use_credits),
          href: linkToHrefWithHost(appLinks.repair),
        },
      ]
    : [
        {
          name: t(power_up_layout.home),
          href: linkToHrefWithHost(rootLinks.home),
        },
        {
          name: t(power_up_layout.ai),
          href: linkToHrefWithHost(rootLinks.ai),
        },
        {
          name: t(power_up_layout.about_us),
          href: linkToHrefWithHost(rootLinks.about),
        },
        {
          name: t(power_up_layout.contact_us),
          href: linkToHrefWithHost(rootLinks.contact),
        },
        {
          name: t(power_up_layout.marketplace),
          href: linkToHrefWithHost(appLinks.home),
        },
      ];
});

type AccountMenuLink = {
  key: string;
  name: string;
  href: string;
  icon: string;
  rel?: string;
  danger?: boolean;
  divideBefore?: boolean;
};

/**
 * Items rendered inside the desktop account hamburger and at the bottom of
 * the mobile menu. Account + Support are local routes on the account
 * subdomain; Sign Out hits the Kratos logout endpoint and ends the session.
 * `danger: true` styles Sign Out in error/red to flag the destructive
 * nature of the action. Admin console appears only for site admins.
 */
const accountMenuLinks = computed((): AccountMenuLink[] => {
  if (!props.loggedIn) return [];
  const session = sessionQuery.data.value;
  const links: AccountMenuLink[] = [
    {
      key: "account",
      name: t(power_up_layout.account),
      href: linkToHrefWithHost(accountLinks.home),
      icon: "mdi-account-circle-outline",
    },
    {
      key: "support",
      name: t(power_up_layout.support),
      href: linkToHrefWithHost(accountLinks.support),
      icon: "mdi-lifebuoy",
    },
  ];
  if (session && isAdminFromSession(session)) {
    links.push({
      key: "admin-console",
      name: t(power_up_layout.admin_console),
      href: linkToHrefWithHost(adminLinks.home),
      icon: "mdi-shield-account-outline",
    });
  }
  links.push({
    key: "sign-out",
    name: t(power_up_layout.sign_out),
    href: linkToHrefWithHost(authLinks.logout),
    icon: "mdi-logout-variant",
    rel: "noopener",
    danger: true,
    divideBefore: true,
  });
  return links;
});
</script>

<style scoped>
/*
 * App shell must be flat theme secondary. An older build used a navy gradient here;
 * forcing solid color + no background-image overrides stale cached CSS if present.
 */
.power-up-app-shell-bar {
  background: rgb(var(--v-theme-secondary)) !important;
  background-image: none !important;
}

.power-up-app-bar--app {
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.power-up-app-bar--marketing {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

/* Single flex row; center everything on the 80px cross-axis */
.power-up-app-bar :deep(.v-toolbar__content) {
  align-items: center;
}

.power-up-logo-link {
  color: inherit;
}

.power-up-logo-link:hover {
  text-decoration: none;
}

.nav-logo-img {
  display: block;
  width: auto;
  height: 48px;
  max-width: min(280px, 42vw);
  max-height: min(48px, calc(80px - 24px));
  object-fit: contain;
  object-position: left center;
}

/* Default img object-fit is fill; Chrome often mis-sizes SVG boxes vs Firefox — use contain like .nav-logo-img. */
.footer-logo-img {
  display: block;
  width: 120px;
  height: auto;
  max-width: 100%;
  aspect-ratio: 100.487 / 34.0001;
  object-fit: contain;
  object-position: left center;
}

/*
 * Compact marketing footer. Target: ~88px tall on desktop (single content
 * row + thin copyright strip), stacking on narrow viewports without
 * ballooning vertically.
 *
 * The earlier v-row / v-col + v-divider layout combined Vuetify's default
 * row gutters (24px), col padding (pa-6 = 48px vertical), and three stacked
 * default-height v-btn link items (~120px) to push the footer well over
 * 500px on desktop. This layout drops all of that overhead.
 */
.power-up-footer__inner {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.power-up-footer__row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}

@media (min-width: 600px) {
  .power-up-footer__row {
    flex-direction: row;
    align-items: center;
    gap: 24px;
  }
}

.power-up-footer__brand {
  color: inherit;
  flex: 0 0 auto;
}

.power-up-footer__brand:hover {
  text-decoration: none;
}

.footer-description {
  font-size: 0.8125rem;
  line-height: 1.4;
  opacity: 0.78;
}

.power-up-footer__links {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 20px;
  flex: 1 1 auto;
}

@media (min-width: 600px) {
  .power-up-footer__links {
    justify-content: center;
  }
}

.power-up-footer__link {
  color: rgb(var(--v-theme-onMarketingFooter));
  text-decoration: none;
  font-size: 0.875rem;
  line-height: 1.4;
  padding: 4px 0;
  opacity: 0.85;
  transition: opacity 120ms ease;
}

.power-up-footer__link:hover,
.power-up-footer__link:focus-visible {
  opacity: 1;
  text-decoration: underline;
}

.power-up-footer__cta {
  flex: 0 0 auto;
}

.power-up-footer__copyright {
  font-size: 0.75rem;
  opacity: 0.6;
  padding-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  margin-top: 4px;
}

/*
 * Desktop content frame: cap main content width so pages don't stretch
 * edge-to-edge on wide monitors.
 *
 * Horizontal inset: mobile/tablet need gutters (below 1440px there is no outer
 * container padding otherwise). Desktop keeps a modest 16px inset inside the
 * 1440px cap (~1408px content width).
 *
 * Pages that need to span the entire viewport (full-bleed hero etc.) can
 * opt out by adding `power-up-main-frame--full-bleed` to this element.
 */
.power-up-main-frame {
  box-sizing: border-box;
  width: 100%;
  max-width: 1440px;
  margin-inline: auto;
  padding-inline: clamp(12px, 4vw, 16px);
}

.power-up-main-frame--full-bleed {
  padding-inline: 0;
}
</style>
